/**
 * SpecificationsDashboard.js
 *
 * Full-featured table for Technical Specifications.
 * - Row click → opens PDF viewer
 * - Top-right: Import, Export, Add Specification
 * - Inline edit, batch delete via checkboxes, references panel
 * - SharePoint-backed PDF storage
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../../utils/apiClient';
import { usePresence, ActiveViewers } from '../../common/ViewingTemplate';
import useColumnOrder from '../../../hooks/useColumnOrder';
import ColumnConfigPanel from '../../common/ColumnConfigPanel';
import { applyColumnOrder } from '../../../utils/columnLayout';

const API_BASE = '/technical-specifications';

const REF_TYPES = [
  { value: 'iso', label: 'ISO Drawing' },
  { value: 'sheet', label: 'Technical Sheet' },
  { value: 'rfi', label: 'RFI' },
  { value: 'photo', label: 'Photo' },
  { value: 'material', label: 'Material' },
  { value: 'transmittal', label: 'Transmittal' },
  { value: 'other', label: 'Other' },
];

const COLUMNS = [
  { key: 'spec_number', label: 'Spec Number', width: 140, minWidth: 80 },
  { key: 'title', label: 'Title', width: 260, minWidth: 120 },
  { key: 'revision', label: 'Revision', width: 90, minWidth: 60 },
  { key: 'discipline', label: 'Discipline', width: 140, minWidth: 80 },
  { key: 'category', label: 'Category', width: 140, minWidth: 80 },
  { key: 'status', label: 'Status', width: 110, minWidth: 70 },
  { key: 'description', label: 'Description', width: 260, minWidth: 120 },
  { key: 'issued_date', label: 'Issued Date', width: 120, minWidth: 90 },
  { key: 'received_date', label: 'Received Date', width: 130, minWidth: 90 },
  { key: 'refs', label: 'References', width: 160, minWidth: 100 },
  { key: 'created_by', label: 'Created By', width: 150, minWidth: 80 },
  { key: 'created_at', label: 'Created At', width: 140, minWidth: 90 },
];

const DEFAULT_VISIBLE = COLUMNS.reduce((acc, c) => ({ ...acc, [c.key]: true }), {});

const SpecificationsDashboard = ({ projectId }) => {
  const { instance, accounts } = useMsal();
  const { viewers, isConnected, connectionState } = usePresence('technicalSpecs', projectId || 'global', true);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [toast, setToast] = useState(null);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showColumnConfig, setShowColumnConfig] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE);
  const { columnOrder, moveColumn } = useColumnOrder('specifications-column-order', COLUMNS);
  const [columnWidths, setColumnWidths] = useState(COLUMNS.reduce((acc, c) => ({ ...acc, [c.key]: c.width }), {}));
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importMode, setImportMode] = useState('excel');
  const [importFile, setImportFile] = useState(null);
  const [batchFiles, setBatchFiles] = useState([]);
  const [importing, setImporting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [addForm, setAddForm] = useState({ spec_number: '', title: '', revision: '', discipline: '', category: '', status: 'Active', description: '', issued_date: '', received_date: '' });
  const [addFile, setAddFile] = useState(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showImportMenu, setShowImportMenu] = useState(false);

  // PDF viewer
  const [viewerSpecId, setViewerSpecId] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);

  // Refs modal
  const [refsSpecId, setRefsSpecId] = useState(null);
  const [refForm, setRefForm] = useState({ linked_item_type: 'iso', linked_item_id: '', description: '' });
  const [savingRef, setSavingRef] = useState(false);

  const [sortField, setSortField] = useState('spec_number');
  const [sortDir, setSortDir] = useState('asc');

  const tableRef = useRef(null);
  const fileInputRef = useRef(null);
  const addFileRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = () => { setShowExportMenu(false); setShowImportMenu(false); };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchItems = useCallback(async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      const result = await authenticatedApiCall(instance, accounts, 'GET', `${API_BASE}/project/${projectId}`);
      if (result.data?.success) setItems(result.data.data || []);
    } catch (err) {
      console.error('Error fetching specifications:', err);
      showToast('Failed to load specifications', 'error');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, projectId]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // ---- CRUD ----

  const handleAdd = async () => {
    if (!addFile) { showToast('Please select a PDF file', 'error'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', addFile);
      Object.entries(addForm).forEach(([k, v]) => { if (v) formData.append(k, v); });
      await authenticatedApiCall(instance, accounts, 'POST', `${API_BASE}/project/${projectId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setShowAddModal(false);
      setAddForm({ spec_number: '', title: '', revision: '', discipline: '', category: '', status: 'Active', description: '', issued_date: '', received_date: '' });
      setAddFile(null);
      fetchItems();
      showToast('Specification added');
    } catch (err) {
      console.error('Add spec error:', err);
      showToast('Failed to add specification', 'error');
    } finally {
      setUploading(false);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    const form = {};
    COLUMNS.filter(c => c.key !== 'refs' && c.key !== 'created_by' && c.key !== 'created_at').forEach(col => {
      let val = item[col.key] || '';
      if ((col.key === 'issued_date' || col.key === 'received_date') && val) {
        val = new Date(val).toISOString().slice(0, 10);
      }
      form[col.key] = val;
    });
    setEditForm(form);
  };

  const handleSave = async () => {
    try {
      await authenticatedApiCall(instance, accounts, 'PUT', `${API_BASE}/spec/${editingId}`, editForm);
      setEditingId(null);
      fetchItems();
      showToast('Saved');
    } catch (err) {
      showToast('Failed to save', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this specification?')) return;
    try {
      await authenticatedApiCall(instance, accounts, 'DELETE', `${API_BASE}/spec/${id}`);
      fetchItems();
      showToast('Deleted');
    } catch (err) {
      showToast('Failed to delete', 'error');
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedRows.size} selected specifications?`)) return;
    try {
      await authenticatedApiCall(instance, accounts, 'POST', `${API_BASE}/project/${projectId}/batch-delete`, {
        ids: Array.from(selectedRows)
      });
      setSelectedRows(new Set());
      fetchItems();
      showToast(`Deleted ${selectedRows.size} specifications`);
    } catch (err) {
      showToast('Failed to delete', 'error');
    }
  };

  // ---- EXPORT ----

  const handleExportExcel = async () => {
    setShowExportMenu(false);
    try {
      const result = await authenticatedApiCall(instance, accounts, 'GET', `${API_BASE}/project/${projectId}/export`, null, { responseType: 'blob' });
      const blob = new Blob([result.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `specifications_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      showToast('Exported to Excel');
    } catch (err) {
      showToast('Failed to export Excel', 'error');
    }
  };

  const handleExportPdfs = async (scope) => {
    setShowExportMenu(false);
    try {
      const ids = scope === 'selected' ? Array.from(selectedRows) : undefined;
      if (scope === 'selected' && !ids.length) {
        showToast('Select at least one specification to export', 'error');
        return;
      }
      showToast('Preparing PDF download...', 'success');
      const result = await authenticatedApiCall(instance, accounts, 'POST',
        `${API_BASE}/project/${projectId}/export-pdfs`,
        ids ? { ids } : {},
        { responseType: 'blob' }
      );
      const ct = result.headers?.['content-type'] || '';
      const isPdf = ct.includes('pdf');
      const blob = new Blob([result.data], { type: ct || 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = isPdf
        ? `specification.pdf`
        : `specifications_${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
      showToast(isPdf ? 'PDF downloaded' : 'PDFs exported as ZIP');
    } catch (err) {
      showToast('Failed to export PDFs', 'error');
    }
  };

  // ---- IMPORT ----

  const handleImportExcel = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      await authenticatedApiCall(instance, accounts, 'POST', `${API_BASE}/project/${projectId}/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setShowImportModal(false);
      setImportFile(null);
      fetchItems();
      showToast('Excel import successful');
    } catch (err) {
      showToast('Failed to import Excel', 'error');
    } finally {
      setImporting(false);
    }
  };

  const handleImportBatchPdfs = async () => {
    if (!batchFiles.length) return;
    setImporting(true);
    try {
      const formData = new FormData();
      batchFiles.forEach(f => formData.append('files', f));
      const result = await authenticatedApiCall(instance, accounts, 'POST',
        `${API_BASE}/project/${projectId}/batch-upload`, formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setShowImportModal(false);
      setBatchFiles([]);
      fetchItems();
      showToast(`Uploaded ${result.data?.count || batchFiles.length} PDFs`);
    } catch (err) {
      showToast('Failed to batch upload PDFs', 'error');
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const result = await authenticatedApiCall(instance, accounts, 'GET', `${API_BASE}/project/${projectId}/template`, null, { responseType: 'blob' });
      const blob = new Blob([result.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'specifications_template.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
      showToast('Template downloaded');
    } catch (err) {
      showToast('Failed to download template', 'error');
    }
  };

  // ---- PDF VIEWER ----

  const openPdf = useCallback(async (specId) => {
    if (!specId || !projectId) return;
    setViewerSpecId(specId);
    setLoadingPdf(true);
    try {
      const result = await authenticatedApiCall(instance, accounts, 'GET', `${API_BASE}/spec/${specId}/file`, null, { responseType: 'blob' });
      const blob = new Blob([result.data], { type: 'application/pdf' });
      setPdfUrl(URL.createObjectURL(blob));
    } catch (err) {
      console.error('Open PDF error:', err);
      showToast('Failed to load PDF', 'error');
      setViewerSpecId(null);
    } finally {
      setLoadingPdf(false);
    }
  }, [instance, accounts, projectId]);

  const closePdf = () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setViewerSpecId(null);
  };

  // ---- REFERENCES ----

  const currentRefs = useMemo(() => {
    if (!refsSpecId) return [];
    const item = items.find(i => i.id === refsSpecId);
    return item?.refs || [];
  }, [refsSpecId, items]);

  const handleAddRef = async () => {
    if (!refForm.linked_item_type) return;
    setSavingRef(true);
    try {
      await authenticatedApiCall(instance, accounts, 'POST', `${API_BASE}/spec/${refsSpecId}/references`, refForm);
      setRefForm({ linked_item_type: 'iso', linked_item_id: '', description: '' });
      fetchItems();
      showToast('Reference added');
    } catch (err) {
      showToast('Failed to add reference', 'error');
    } finally {
      setSavingRef(false);
    }
  };

  const handleDeleteRef = async (refId) => {
    try {
      await authenticatedApiCall(instance, accounts, 'DELETE', `${API_BASE}/references/${refId}`);
      fetchItems();
      showToast('Reference removed');
    } catch (err) {
      showToast('Failed to remove reference', 'error');
    }
  };

  // ---- TABLE UTILS ----

  const autoFitColumns = useCallback(() => {
    const CHAR_PX = 8;
    const CELL_PADDING = 24;
    const next = {};
    COLUMNS.forEach(col => {
      const headerLen = col.label.length;
      const maxCell = items.length
        ? Math.max(0, ...items.map(r => {
            if (col.key === 'refs') return (r.refs?.length || 0) > 0 ? String(r.refs.length) + ' refs' : '—';
            return String(r[col.key] ?? '').length;
          }))
        : 0;
      const contentChars = Math.max(headerLen, maxCell, 1);
      next[col.key] = Math.max(col.minWidth, Math.ceil(contentChars * CHAR_PX + CELL_PADDING));
    });
    setColumnWidths(next);
    showToast('Columns auto-fitted');
  }, [items]);

  const toggleSelectAll = () => {
    if (selectedRows.size === filtered.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(filtered.map(i => i.id)));
    }
  };

  const toggleSelect = (id) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const filtered = useMemo(() => {
    let list = items;
    if (search) {
      const s = search.toLowerCase();
      list = list.filter(i =>
        (i.spec_number || '').toLowerCase().includes(s) ||
        (i.title || '').toLowerCase().includes(s) ||
        (i.discipline || '').toLowerCase().includes(s) ||
        (i.category || '').toLowerCase().includes(s) ||
        (i.status || '').toLowerCase().includes(s) ||
        (i.description || '').toLowerCase().includes(s) ||
        (i.created_by || '').toLowerCase().includes(s)
      );
    }
    list = [...list].sort((a, b) => {
      let av = a[sortField] ?? '';
      let bv = b[sortField] ?? '';
      if (sortField === 'refs') {
        av = (a.refs?.length || 0);
        bv = (b.refs?.length || 0);
      }
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [items, search, sortField, sortDir]);

  const visibleCols = useMemo(() => applyColumnOrder(COLUMNS, columnOrder).filter(c => visibleColumns[c.key]), [visibleColumns, columnOrder]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') setEditingId(null);
  };

  const formatDate = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('en-CA'); } catch { return d; }
  };

  const formatDateTime = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleString('en-CA', { dateStyle: 'short', timeStyle: 'short' }); } catch { return d; }
  };

  const renderCellValue = (col, item) => {
    if (col.key === 'refs') {
      const refs = item.refs || [];
      if (!refs.length) return <span style={{ color: '#d1d5db' }}>—</span>;
      return (
        <span
          style={{ color: '#2563eb', cursor: 'pointer', textDecoration: 'underline' }}
          onClick={(e) => { e.stopPropagation(); setRefsSpecId(item.id); }}
        >
          {refs.length} ref{refs.length !== 1 ? 's' : ''}
        </span>
      );
    }
    if (col.key === 'issued_date' || col.key === 'received_date') return formatDate(item[col.key]);
    if (col.key === 'created_at') return formatDateTime(item[col.key]);
    if (col.key === 'status') {
      const st = item.status || '';
      const color = st === 'Active' ? '#16a34a' : st === 'Superseded' ? '#d97706' : st === 'Cancelled' ? '#dc2626' : '#6b7280';
      return <span style={{ color, fontWeight: 500 }}>{st || '—'}</span>;
    }
    return item[col.key] || <span style={{ color: '#d1d5db' }}>—</span>;
  };

  const cellStyle = { padding: '8px 10px', fontSize: '0.85rem', borderBottom: '1px solid #e5e7eb' };
  const inputStyle = { padding: '5px 8px', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '0.85rem', width: '100%', boxSizing: 'border-box' };

  // ==================== RENDER ====================

  // PDF Viewer overlay
  if (viewerSpecId) {
    return (
      <div style={{ position: 'absolute', inset: 0, zIndex: 100, backgroundColor: '#f3f4f6', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px', backgroundColor: '#1e293b', color: 'white' }}>
          <span style={{ fontWeight: 600 }}>
            {items.find(i => i.id === viewerSpecId)?.spec_number || 'Specification'} — {items.find(i => i.id === viewerSpecId)?.title || 'PDF Viewer'}
          </span>
          <button onClick={closePdf} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.3)', color: 'white', padding: '6px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem' }}>
            Close
          </button>
        </div>
        <div style={{ flex: 1 }}>
          {loadingPdf ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#6b7280' }}>Loading PDF...</div>
          ) : pdfUrl ? (
            <iframe src={pdfUrl} title="Spec PDF" style={{ width: '100%', height: '100%', border: 'none' }} />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#dc2626' }}>Failed to load PDF</div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: '20px', right: '20px', zIndex: 9999, padding: '12px 20px',
          borderRadius: '8px', color: 'white', fontWeight: '500',
          backgroundColor: toast.type === 'error' ? '#dc3545' : '#198754',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }}>
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem', flexShrink: 0 }}>
        <div>
          <h2 style={{ margin: 0, color: '#333', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            Specifications
          </h2>
          <p style={{ margin: '0.5rem 0 0', color: '#666', fontSize: '0.9rem' }}>
            {filtered.length} specification{filtered.length !== 1 ? 's' : ''}{search ? ' (filtered)' : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={4} showConnectionStatus={true} />
          <button onClick={autoFitColumns} title="Auto-fit column widths" style={toolbarBtnStyle}>Auto-fit</button>

          {/* Import dropdown */}
          <div style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
            <button onClick={() => { setShowImportMenu(p => !p); setShowExportMenu(false); }} style={toolbarBtnStyle} title="Import">
              Import ▾
            </button>
            {showImportMenu && (
              <div style={dropdownMenuStyle}>
                <button style={dropdownItemStyle} onClick={() => { setShowImportMenu(false); setImportMode('excel'); setShowImportModal(true); }}>
                  Import Excel
                </button>
                <button style={dropdownItemStyle} onClick={() => { setShowImportMenu(false); setImportMode('pdf'); setShowImportModal(true); }}>
                  Batch Upload PDFs
                </button>
              </div>
            )}
          </div>

          {/* Export dropdown */}
          <div style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
            <button onClick={() => { setShowExportMenu(p => !p); setShowImportMenu(false); }} style={toolbarBtnStyle} title="Export">
              Export ▾
            </button>
            {showExportMenu && (
              <div style={dropdownMenuStyle}>
                <button style={dropdownItemStyle} onClick={handleExportExcel}>
                  Export as Excel
                </button>
                <button style={dropdownItemStyle} onClick={() => handleExportPdfs('all')}>
                  Export All PDFs (ZIP)
                </button>
                {selectedRows.size > 0 && (
                  <button style={dropdownItemStyle} onClick={() => handleExportPdfs('selected')}>
                    Export Selected PDFs ({selectedRows.size})
                  </button>
                )}
              </div>
            )}
          </div>

          <button onClick={() => setShowColumnConfig(!showColumnConfig)} style={toolbarBtnStyle} title="Column visibility">Columns</button>
          <button onClick={() => setShowAddModal(true)} style={{ ...toolbarBtnStyle, backgroundColor: '#dc3545', color: 'white', border: '1px solid #dc3545' }}>+ Add Specification</button>
          {selectedRows.size > 0 && (
            <button onClick={handleBulkDelete} style={{ ...toolbarBtnStyle, backgroundColor: '#dc3545', color: 'white', border: '1px solid #dc3545' }} title="Delete selected">
              Delete ({selectedRows.size})
            </button>
          )}
          <button onClick={() => fetchItems()} style={toolbarBtnStyle} title="Refresh data">Refresh</button>
        </div>
      </div>

      {/* Search bar */}
      <div style={{ backgroundColor: '#f8f9fa', padding: '0.75rem 1rem', borderRadius: '6px', marginBottom: '1rem', display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'center', flexShrink: 0 }}>
        <div>
          <label style={{ display: 'block', fontSize: '0.8rem', color: '#666', marginBottom: '0.25rem' }}>Search</label>
          <input
            type="text" placeholder="Search all columns..." value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.85rem', width: '280px', outline: 'none' }}
          />
        </div>
        <div style={{ fontSize: '0.85rem', color: '#6b7280', alignSelf: 'flex-end', paddingBottom: '4px' }}>
          {filtered.length} of {items.length} records
        </div>
      </div>

      {/* Column Config Modal */}
      {showColumnConfig && (
        <div style={modalOverlayStyle} onClick={() => setShowColumnConfig(false)}>
          <div style={modalContentStyle} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', borderBottom: '1px solid #dee2e6', paddingBottom: '0.75rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Column Configuration</h3>
              <button onClick={() => setShowColumnConfig(false)} style={closeBtnStyle}>&times;</button>
            </div>
            <ColumnConfigPanel
              columns={applyColumnOrder(COLUMNS, columnOrder)}
              isVisible={(key) => !!visibleColumns[key]}
              onToggle={(key) => setVisibleColumns(prev => ({ ...prev, [key]: !prev[key] }))}
              onMove={moveColumn}
              onShowAll={() => setVisibleColumns(COLUMNS.reduce((acc, c) => ({ ...acc, [c.key]: true }), {}))}
              onHideAll={() => setVisibleColumns(COLUMNS.reduce((acc, c) => ({ ...acc, [c.key]: false }), {}))}
              compact={false}
            />
            <div style={{ marginTop: '1rem', textAlign: 'right' }}>
              <button onClick={() => setShowColumnConfig(false)} style={{ padding: '0.5rem 1rem', backgroundColor: '#198754', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Done</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Specification Modal */}
      {showAddModal && (
        <div style={modalOverlayStyle} onClick={() => { setShowAddModal(false); setAddFile(null); }}>
          <div style={{ ...modalContentStyle, maxWidth: '600px' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', borderBottom: '1px solid #dee2e6', paddingBottom: '0.75rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Add Specification</h3>
              <button onClick={() => { setShowAddModal(false); setAddFile(null); }} style={closeBtnStyle}>&times;</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              {[
                { key: 'spec_number', label: 'Spec Number' },
                { key: 'title', label: 'Title' },
                { key: 'revision', label: 'Revision' },
                { key: 'discipline', label: 'Discipline' },
                { key: 'category', label: 'Category' },
                { key: 'status', label: 'Status' },
                { key: 'issued_date', label: 'Issued Date', type: 'date' },
                { key: 'received_date', label: 'Received Date', type: 'date' },
              ].map(f => (
                <div key={f.key}>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: '#555', marginBottom: '0.25rem' }}>{f.label}</label>
                  {f.key === 'status' ? (
                    <select value={addForm.status} onChange={e => setAddForm(p => ({ ...p, status: e.target.value }))} style={{ ...inputStyle, padding: '7px 8px' }}>
                      <option value="Active">Active</option>
                      <option value="Superseded">Superseded</option>
                      <option value="Cancelled">Cancelled</option>
                      <option value="Draft">Draft</option>
                    </select>
                  ) : (
                    <input
                      type={f.type || 'text'} value={addForm[f.key]}
                      onChange={e => setAddForm(p => ({ ...p, [f.key]: e.target.value }))}
                      style={inputStyle}
                    />
                  )}
                </div>
              ))}
            </div>
            <div style={{ marginTop: '0.75rem' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', color: '#555', marginBottom: '0.25rem' }}>Description</label>
              <textarea
                value={addForm.description}
                onChange={e => setAddForm(p => ({ ...p, description: e.target.value }))}
                style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }}
              />
            </div>
            <div style={{ marginTop: '0.75rem' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', color: '#555', marginBottom: '0.25rem' }}>PDF File *</label>
              <input ref={addFileRef} type="file" accept=".pdf" onChange={e => setAddFile(e.target.files[0])} style={{ fontSize: '0.85rem' }} />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button onClick={() => { setShowAddModal(false); setAddFile(null); }} style={toolbarBtnStyle}>Cancel</button>
              <button onClick={handleAdd} disabled={uploading || !addFile} style={{ ...toolbarBtnStyle, backgroundColor: '#198754', color: 'white', border: '1px solid #198754', opacity: (uploading || !addFile) ? 0.6 : 1 }}>
                {uploading ? 'Uploading...' : 'Add Specification'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal (Excel or Batch PDF) */}
      {showImportModal && (
        <div style={modalOverlayStyle} onClick={() => { setShowImportModal(false); setImportFile(null); setBatchFiles([]); }}>
          <div style={{ ...modalContentStyle, maxWidth: '520px' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', borderBottom: '1px solid #dee2e6', paddingBottom: '0.75rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem' }}>
                {importMode === 'excel' ? 'Import from Excel' : 'Batch Upload PDFs'}
              </h3>
              <button onClick={() => { setShowImportModal(false); setImportFile(null); setBatchFiles([]); }} style={closeBtnStyle}>&times;</button>
            </div>

            {/* Tab toggle */}
            <div style={{ display: 'flex', gap: '0', marginBottom: '1rem', borderRadius: '6px', overflow: 'hidden', border: '1px solid #d1d5db' }}>
              <button
                onClick={() => { setImportMode('excel'); setBatchFiles([]); }}
                style={{ flex: 1, padding: '8px', border: 'none', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500,
                  backgroundColor: importMode === 'excel' ? '#374151' : '#f9fafb',
                  color: importMode === 'excel' ? 'white' : '#374151'
                }}>
                Import Excel
              </button>
              <button
                onClick={() => { setImportMode('pdf'); setImportFile(null); }}
                style={{ flex: 1, padding: '8px', border: 'none', borderLeft: '1px solid #d1d5db', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500,
                  backgroundColor: importMode === 'pdf' ? '#374151' : '#f9fafb',
                  color: importMode === 'pdf' ? 'white' : '#374151'
                }}>
                Batch Upload PDFs
              </button>
            </div>

            {importMode === 'excel' ? (
              <>
                <p style={{ fontSize: '0.9rem', color: '#555', marginBottom: '1rem' }}>
                  Upload an Excel file (.xlsx) with columns matching the specifications fields (metadata only, no PDFs).
                </p>
                <button onClick={handleDownloadTemplate} style={{ ...toolbarBtnStyle, marginBottom: '1rem' }}>Download Template</button>
                <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={e => setImportFile(e.target.files[0])} style={{ marginBottom: '1rem', display: 'block' }} />
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                  <button onClick={() => { setShowImportModal(false); setImportFile(null); }} style={toolbarBtnStyle}>Cancel</button>
                  <button onClick={handleImportExcel} disabled={!importFile || importing}
                    style={{ ...toolbarBtnStyle, backgroundColor: '#198754', color: 'white', border: '1px solid #198754', opacity: (!importFile || importing) ? 0.6 : 1 }}>
                    {importing ? 'Importing...' : 'Import Excel'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p style={{ fontSize: '0.9rem', color: '#555', marginBottom: '1rem' }}>
                  Select one or more PDF files. Each PDF will create a new specification entry. You can edit metadata (spec number, title, etc.) after upload.
                </p>
                <div
                  onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#2563eb'; }}
                  onDragLeave={e => { e.currentTarget.style.borderColor = '#d1d5db'; }}
                  onDrop={e => {
                    e.preventDefault();
                    e.currentTarget.style.borderColor = '#d1d5db';
                    const files = Array.from(e.dataTransfer.files).filter(f => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));
                    if (files.length) setBatchFiles(prev => [...prev, ...files]);
                  }}
                  style={{ border: '2px dashed #d1d5db', borderRadius: '8px', padding: '1.5rem', textAlign: 'center', marginBottom: '1rem', cursor: 'pointer', backgroundColor: '#fafbfc' }}
                  onClick={() => document.getElementById('batch-pdf-input')?.click()}
                >
                  <p style={{ color: '#6b7280', fontSize: '0.9rem', margin: 0 }}>
                    Drag & drop PDF files here, or click to browse
                  </p>
                  <input id="batch-pdf-input" type="file" accept=".pdf" multiple style={{ display: 'none' }}
                    onChange={e => { setBatchFiles(prev => [...prev, ...Array.from(e.target.files)]); e.target.value = ''; }} />
                </div>
                {batchFiles.length > 0 && (
                  <div style={{ maxHeight: '150px', overflowY: 'auto', marginBottom: '1rem', border: '1px solid #e5e7eb', borderRadius: '6px' }}>
                    {batchFiles.map((f, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', borderBottom: '1px solid #f3f4f6', fontSize: '0.85rem' }}>
                        <span style={{ color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '300px' }}>{f.name}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>{(f.size / 1024 / 1024).toFixed(1)} MB</span>
                          <button onClick={() => setBatchFiles(prev => prev.filter((_, j) => j !== i))}
                            style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '1rem', lineHeight: 1 }}>&times;</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>{batchFiles.length} file{batchFiles.length !== 1 ? 's' : ''} selected</span>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button onClick={() => { setShowImportModal(false); setBatchFiles([]); }} style={toolbarBtnStyle}>Cancel</button>
                    <button onClick={handleImportBatchPdfs} disabled={!batchFiles.length || importing}
                      style={{ ...toolbarBtnStyle, backgroundColor: '#198754', color: 'white', border: '1px solid #198754', opacity: (!batchFiles.length || importing) ? 0.6 : 1 }}>
                      {importing ? 'Uploading...' : `Upload ${batchFiles.length} PDF${batchFiles.length !== 1 ? 's' : ''}`}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* References Modal */}
      {refsSpecId && (
        <div style={modalOverlayStyle} onClick={() => setRefsSpecId(null)}>
          <div style={{ ...modalContentStyle, maxWidth: '560px' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', borderBottom: '1px solid #dee2e6', paddingBottom: '0.75rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem' }}>
                References — {items.find(i => i.id === refsSpecId)?.spec_number || 'Spec'}
              </h3>
              <button onClick={() => setRefsSpecId(null)} style={closeBtnStyle}>&times;</button>
            </div>
            {currentRefs.length === 0 ? (
              <p style={{ color: '#999', fontSize: '0.9rem' }}>No references yet.</p>
            ) : (
              <div style={{ maxHeight: '250px', overflowY: 'auto', marginBottom: '1rem' }}>
                {currentRefs.map(ref => (
                  <div key={ref.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', borderBottom: '1px solid #f3f4f6' }}>
                    <div>
                      <span style={{ fontSize: '0.85rem', fontWeight: 500, color: '#374151' }}>
                        {REF_TYPES.find(r => r.value === ref.linked_item_type)?.label || ref.linked_item_type}
                      </span>
                      {ref.linked_item_id && <span style={{ fontSize: '0.8rem', color: '#6b7280', marginLeft: '8px' }}>#{ref.linked_item_id}</span>}
                      {ref.description && <span style={{ fontSize: '0.8rem', color: '#9ca3af', marginLeft: '8px' }}>— {ref.description}</span>}
                    </div>
                    <button onClick={() => handleDeleteRef(ref.id)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '0.8rem' }}>Remove</button>
                  </div>
                ))}
              </div>
            )}
            <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '0.75rem' }}>
              <p style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.5rem', fontWeight: 600 }}>Add Reference</p>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <select value={refForm.linked_item_type} onChange={e => setRefForm(p => ({ ...p, linked_item_type: e.target.value }))}
                  style={{ ...inputStyle, width: '130px', padding: '6px 8px' }}>
                  {REF_TYPES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
                <input placeholder="ID / Number" value={refForm.linked_item_id} onChange={e => setRefForm(p => ({ ...p, linked_item_id: e.target.value }))}
                  style={{ ...inputStyle, width: '120px' }} />
                <input placeholder="Description" value={refForm.description} onChange={e => setRefForm(p => ({ ...p, description: e.target.value }))}
                  style={{ ...inputStyle, flex: 1 }} />
                <button onClick={handleAddRef} disabled={savingRef}
                  style={{ ...toolbarBtnStyle, backgroundColor: '#2563eb', color: 'white', border: '1px solid #2563eb', opacity: savingRef ? 0.6 : 1, whiteSpace: 'nowrap' }}>
                  {savingRef ? 'Adding...' : 'Add'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div ref={tableRef} style={{ backgroundColor: 'white', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflowX: 'auto', flex: 1 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
          <thead>
            <tr style={{ backgroundColor: '#f9fafb', position: 'sticky', top: 0, zIndex: 2 }}>
              <th style={{ ...cellStyle, width: '40px', textAlign: 'center' }}>
                <input type="checkbox" checked={filtered.length > 0 && selectedRows.size === filtered.length} onChange={toggleSelectAll} />
              </th>
              {visibleCols.map(col => (
                <th key={col.key}
                  onClick={() => col.key !== 'refs' && handleSort(col.key)}
                  style={{
                    ...cellStyle, fontWeight: '600', fontSize: '0.75rem', color: '#6b7280',
                    textTransform: 'uppercase', minWidth: `${columnWidths[col.key]}px`,
                    width: `${columnWidths[col.key]}px`, borderBottom: '2px solid #e5e7eb',
                    cursor: col.key !== 'refs' ? 'pointer' : 'default', userSelect: 'none'
                  }}>
                  {col.label}
                  {sortField === col.key && <span style={{ marginLeft: '4px' }}>{sortDir === 'asc' ? '▲' : '▼'}</span>}
                </th>
              ))}
              <th style={{ ...cellStyle, width: '120px', borderBottom: '2px solid #e5e7eb' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={visibleCols.length + 2} style={{ padding: '60px', textAlign: 'center', color: '#6b7280' }}>Loading specifications...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={visibleCols.length + 2} style={{ padding: '60px', textAlign: 'center', color: '#6b7280' }}>
                {search ? 'No matching specifications found.' : 'No specifications yet. Click "+ Add Specification" to create one.'}
              </td></tr>
            ) : filtered.map(item => {
              const isEditing = editingId === item.id;
              const hasFile = !!item.sharepoint_item_id;
              return (
                <tr key={item.id}
                  style={{
                    borderBottom: '1px solid #f3f4f6',
                    backgroundColor: selectedRows.has(item.id) ? '#eff6ff' : 'transparent',
                    cursor: hasFile && !isEditing ? 'pointer' : 'default'
                  }}
                  onClick={() => { if (hasFile && !isEditing) openPdf(item.id); }}
                  onMouseEnter={(e) => { if (!isEditing && !selectedRows.has(item.id)) e.currentTarget.style.backgroundColor = '#fafbfc'; }}
                  onMouseLeave={(e) => { if (!isEditing && !selectedRows.has(item.id)) e.currentTarget.style.backgroundColor = selectedRows.has(item.id) ? '#eff6ff' : 'transparent'; }}
                >
                  <td style={{ ...cellStyle, textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={selectedRows.has(item.id)} onChange={() => toggleSelect(item.id)} />
                  </td>
                  {visibleCols.map(col => {
                    const editable = col.key !== 'refs' && col.key !== 'created_by' && col.key !== 'created_at';
                    if (isEditing && editable) {
                      return (
                        <td key={col.key} style={cellStyle} onClick={e => e.stopPropagation()}>
                          {col.key === 'description' ? (
                            <textarea style={{ ...inputStyle, minHeight: '50px', resize: 'vertical' }}
                              value={editForm[col.key] || ''} onChange={e => setEditForm({ ...editForm, [col.key]: e.target.value })}
                              onKeyDown={e => { if (e.key === 'Escape') setEditingId(null); }} />
                          ) : col.key === 'status' ? (
                            <select value={editForm.status || ''} onChange={e => setEditForm({ ...editForm, status: e.target.value })} style={{ ...inputStyle, padding: '6px 8px' }}>
                              <option value="Active">Active</option>
                              <option value="Superseded">Superseded</option>
                              <option value="Cancelled">Cancelled</option>
                              <option value="Draft">Draft</option>
                            </select>
                          ) : col.key === 'issued_date' || col.key === 'received_date' ? (
                            <input type="date" value={editForm[col.key] || ''} onChange={e => setEditForm({ ...editForm, [col.key]: e.target.value })} style={inputStyle} onKeyDown={handleKeyDown} />
                          ) : (
                            <input type="text" style={inputStyle} value={editForm[col.key] || ''} onChange={e => setEditForm({ ...editForm, [col.key]: e.target.value })} onKeyDown={handleKeyDown} />
                          )}
                        </td>
                      );
                    }
                    return (
                      <td key={col.key} style={{
                        ...cellStyle, color: '#374151',
                        maxWidth: `${columnWidths[col.key]}px`, overflow: 'hidden', textOverflow: 'ellipsis',
                        whiteSpace: col.key === 'description' ? 'pre-wrap' : 'nowrap'
                      }}
                        onClick={col.key === 'refs' ? e => e.stopPropagation() : undefined}>
                        {renderCellValue(col, item)}
                      </td>
                    );
                  })}
                  <td style={cellStyle} onClick={e => e.stopPropagation()}>
                    {isEditing ? (
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button onClick={handleSave} style={{ padding: '4px 10px', backgroundColor: '#198754', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' }}>Save</button>
                        <button onClick={() => setEditingId(null)} style={{ padding: '4px 10px', backgroundColor: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' }}>Cancel</button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button onClick={() => startEdit(item)} style={{ padding: '3px 8px', backgroundColor: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' }}>Edit</button>
                        <button onClick={() => { setRefsSpecId(item.id); }} style={{ padding: '3px 8px', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem', color: '#2563eb' }}>Refs</button>
                        <button onClick={() => handleDelete(item.id)} style={{ padding: '3px 8px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem', color: '#dc3545' }}>Del</button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const toolbarBtnStyle = {
  padding: '8px 16px', backgroundColor: 'white', color: '#374151', border: '1px solid #d1d5db',
  borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500',
  display: 'flex', alignItems: 'center', gap: '6px'
};

const smallBtnStyle = {
  padding: '4px 12px', backgroundColor: '#f3f4f6', border: '1px solid #d1d5db',
  borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem'
};

const closeBtnStyle = { background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#666' };

const dropdownMenuStyle = {
  position: 'absolute', top: '100%', left: 0, marginTop: '4px', zIndex: 50,
  backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '6px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.12)', minWidth: '190px', overflow: 'hidden'
};

const dropdownItemStyle = {
  display: 'block', width: '100%', padding: '9px 14px', border: 'none', background: 'none',
  textAlign: 'left', fontSize: '0.85rem', color: '#374151', cursor: 'pointer',
  borderBottom: '1px solid #f3f4f6'
};

const modalOverlayStyle = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
};

const modalContentStyle = {
  backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', maxWidth: '500px',
  width: '90%', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
};

export default SpecificationsDashboard;
